<template>
  <div
    :class="[$style.mainPageBanner, { [$style.mainPageBannerLoading]: isLoading }]"
    :style="{ '--banner-change-time': animationChangeTimeSeconds }"
  >
    <div
      v-if="!isMobileLayout || banners.length === 1"
      :class="$style.mainPageBannerDesktopWrapper"
    >
      <SliderItem
        v-if="bannerToShow"
        :class="$style.mainPageBannerWrapper"
        :is-animating="isAnimating"
        :subtitle-first="bannerToShow.subtitleFirst"
        :subtitle-second="bannerToShow.subtitleSecond"
        :is-without-animation="isWithoutAnimation"
        :finish-date="bannerToShow.finishDate"
        :start-date="bannerToShow.startDate"
        :front-image="bannerToShow.frontImage"
        :button-text="bannerToShow.buttonText"
        :button-action="bannerToShow.bannerAction"
        :disable-button="disableButton"
        @buttonClick="$emit('buttonClick')"
      >
        <template #beforeButtom>
          <slot name="beforeButtom" />
        </template>
      </SliderItem>
      <div v-if="banners.length > 1" :class="$style.mainPageBannerPagination">
        <span
          v-for="(_, i) in banners"
          :key="i"
          :class="[
            $style.mainPageBannerPaginationItem,
            {
              [$style.mainPageBannerPaginationItemActive]: i === currentBannerId,
              [$style.mainPageBannerAnimationPlay]:
                isAnimatingPagination && i !== banners.length - 1,
              [$style.mainPageBannerPaginationDisable]: isAnimating,
            },
          ]"
          @click="paginationClickHandler(i)"
        >
          <span />
        </span>
      </div>
    </div>
    <Flicking
      v-else
      :key="sliderKey"
      :options="{ panelsPerView: 1, circular: true }"
      :class="$style.mainPageBannerSlider"
      @ready="sliderOnReady"
    >
      <div
        v-for="banner in banners"
        :key="banner.subtitleFirst"
        :class="$style.mainPageBannerSliderItem"
      >
        <SliderItem
          :subtitle-first="banner.subtitleFirst"
          :subtitle-second="banner.subtitleSecond"
          :is-without-animation="isWithoutAnimation"
          :front-image="banner.frontImage"
          :button-text="banner.buttonText"
          :button-action="banner.bannerAction"
          @buttonClick="$emit('buttonClick')"
        />
      </div>
    </Flicking>
  </div>
</template>

<script lang="ts" setup>
import { AutoPlay } from '@egjs/flicking-plugins';
import Flicking, { type FlickingEvents } from '@egjs/vue3-flicking';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import '@egjs/vue3-flicking/dist/flicking.css';
import { isNotLighthouse } from '~/utils/optimizations';
import SliderItem from '../mainBanner/mainBanner.vue';
// import MainBannerSliderSkeleton from './mainBannerSliderSkeleton.vue';
import type { SliderBannerData } from '~/types/slider';

const props = defineProps({
  banners: {
    type: Array as PropType<SliderBannerData[]>,
    required: true,
  },
  isWithoutAnimation: {
    type: Boolean,
    default: false,
  },
  disableButton: {
    type: Boolean,
    default: true,
  },
  isLoading: {
    type: Boolean,
    default: true,
  },
});
defineEmits<{
  (e: 'buttonClick'): void;
}>();

const currentBannerId = ref(0);
const bannerChangeTimeout = ref(5000);
const bannerAnimationTime = ref(800); // time for animation on change banners in milliseconds
const isAnimating = ref(false);
const isAnimatingPagination = ref(false);
const sliderKey = ref(Date.now());
const platform = usePlatform();
const bannerChangeInterval = ref<null | ReturnType<typeof setInterval>>(null);

const bannerToShow = computed(() => props.banners[currentBannerId.value]);

const isMobileLayout = computed(() => platform.isMobile);

const bannersTitles = computed(() => props.banners.map((a) => a.subtitleFirst).join(''));

const animationChangeTimeSeconds = computed(() => bannerAnimationTime.value / 1000 + 's');

const startChangeBannersInterval = () => {
  bannerChangeInterval.value = setInterval(() => {
    if (props.banners.length === 1) {
      removeChangeBannersInterval();
    } else {
      changeActiveBanner();
    }
  }, bannerChangeTimeout.value + bannerAnimationTime.value * 2);
};

const removeChangeBannersInterval = () => {
  if (bannerChangeInterval.value) {
    clearInterval(bannerChangeInterval.value);
  }
};

const paginationClickHandler = (num: number) => {
  removeChangeBannersInterval();
  changeActiveBanner(num, false);
  startChangeBannersInterval();
};
const sliderOnReady = (event: FlickingEvents['ready']) => {
  const currentTarget = event.currentTarget;
  currentTarget.addPlugins(
    new AutoPlay({ duration: bannerChangeTimeout.value, stopOnHover: false })
  );
};

const changeActiveBanner = (num?: number, animationPagination = true) => {
  isAnimating.value = true;
  isAnimatingPagination.value = animationPagination;
  setTimeout(() => {
    isAnimating.value = false;
  }, bannerAnimationTime.value + 30);
  setTimeout(() => {
    isAnimatingPagination.value = false;
  }, bannerAnimationTime.value);
  if (num === undefined) {
    setTimeout(() => {
      if (currentBannerId.value >= props.banners.length - 1) {
        currentBannerId.value = 0;
      } else {
        currentBannerId.value++;
      }
    }, bannerAnimationTime.value);
  } else {
    setTimeout(() => {
      currentBannerId.value = num;
    }, bannerAnimationTime.value);
  }
};

watch(
  () => useStoreSsPlayer().isPlayerAuthorised,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      removeChangeBannersInterval();
      currentBannerId.value = 0;
      nextTick(startChangeBannersInterval);
    }
  }
);

watch(
  () => bannersTitles.value,
  (val, oldVal) => {
    if (val !== oldVal) {
      setTimeout(() => (sliderKey.value = Date.now()));
    }
  },
  { immediate: true }
);

onMounted(() => {
  // PAGESPEED HACK\

  // if (props.banners) {
  //   props.banners.forEach((banner) => {
  //     if (banner.frontImage) {
  //       useHead({
  //         link: [
  //           {
  //             rel: 'preload',
  //             href: banner.frontImage.url,
  //             as: 'image',
  //             crossorigin: '',
  //           },
  //         ],
  //       });
  //     }
  //     if (banner.backImage) {
  //       useHead({
  //         link: [
  //           {
  //             rel: 'preload',
  //             href: banner.backImage.url,
  //             as: 'image',
  //             crossorigin: '',
  //           },
  //         ],
  //       });
  //     }
  //   });
  // }

  if (isNotLighthouse()) {
    startChangeBannersInterval();
  }
});

onBeforeUnmount(() => removeChangeBannersInterval());
</script>

<style lang="scss" module src="./mainBannerSlider.scss"></style>
